import { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FEEDBACK_MAX, FEEDBACK_TOPICS, feedbackErrorMessage } from '../../lib/feedback';

// "Tell us" form (063). A plain textarea on purpose: this goes to the team, not
// the community, so no PostEditor (its @mention search hits the server) and no
// draft. Any signed-in member can send; submit_feedback enforces the rest.
const FeedbackModal = ({ topic, onClose }) => {
  useModalDismiss(onClose);
  const copy = FEEDBACK_TOPICS[topic];
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const trimmed = message.trim();

  const handleSend = async () => {
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    const { error: err } = await supabase.rpc('submit_feedback', { p_topic: topic, p_message: trimmed });
    setSending(false);
    if (err) { setError(feedbackErrorMessage(err)); return; }
    setSent(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden animate-fadeIn"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
        onClick={e => e.stopPropagation()}
      >
        {sent ? (
          <div className="p-8 text-center">
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              <CheckCircle size={32} className="text-white" />
            </div>
            <h3 id="feedback-title" className="text-xl font-bold text-primary mb-2">Thank you</h3>
            <p className="text-sm text-tertiary mb-5">The DSP Cafe team reads every one of these.</p>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 id="feedback-title" className="text-lg font-bold text-primary">{copy.title}</h3>
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
                style={{ color: 'var(--text-tertiary)' }}
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-tertiary mb-4">{copy.prompt}</p>

            <label htmlFor="feedback-message" className="sr-only">Your message</label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={FEEDBACK_MAX}
              rows={5}
              autoFocus
              placeholder={copy.placeholder}
              className="w-full rounded-xl px-3 py-2 text-[15px] text-primary resize-none focus:outline-none"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
            />
            <div className="flex items-center justify-between mt-1 mb-3">
              <p className="text-xs text-tertiary">Only the DSP Cafe team sees this.</p>
              <p className="text-xs text-tertiary tabular-nums">{message.length}/{FEEDBACK_MAX}</p>
            </div>

            {error && <p className="text-xs mb-3" style={{ color: '#dc2626' }}>{error}</p>}

            <button
              onClick={handleSend}
              disabled={!trimmed || sending}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {sending ? 'Sending…' : 'Send to the team'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
